import { Fragment, useMemo } from "react";
import { ToggleGroup, ToggleGroupItem, ToggleGroupSeparator } from "@/components/ui/toggle-group";

const BUFFER_MS = 120_000; // 2 minutes

const Buttons = [
    {
        label: "Last Year",
        getDates: () => {
            const now = new Date();
            return {
                startDate: new Date(now.getFullYear() - 1, 0, 1, 0, 0, 0, 0),
                endDate: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999),
            };
        },
    },
    {
        label: "This Year",
        getDates: () => {
            const now = new Date();
            return {
                startDate: new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0),
                endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
            };
        },
    },
    {
        label: "Last Month",
        getDates: () => {
            const now = new Date();
            const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
            const yearOfLastMonth = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
            return {
                startDate: new Date(yearOfLastMonth, lastMonth, 1, 0, 0, 0, 0),
                endDate: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999),
            };
        },
    },
    {
        label: "This Month",
        getDates: () => {
            const now = new Date();
            return {
                startDate: new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0),
                endDate: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999),
            };
        },
    },
    {
        label: "Last Week",
        getDates: () => {
            const now = new Date();
            const startOfWeek = now.getDate() - now.getDay();
            const startDate = new Date(now);
            startDate.setDate(startOfWeek - 7);
            const endDate = new Date(now);
            endDate.setDate(startOfWeek - 1);
            return {
                startDate: new Date(startDate.setHours(0, 0, 0, 0)),
                endDate: new Date(endDate.setHours(23, 59, 59, 999)),
            };
        },
    },
    {
        label: "This Week",
        getDates: () => {
            const now = new Date();
            const startOfWeek = now.getDate() - now.getDay();
            const startDate = new Date(now);
            startDate.setDate(startOfWeek);
            return {
                startDate: new Date(startDate.setHours(0, 0, 0, 0)),
                endDate: new Date(now.setHours(23, 59, 59, 999)),
            };
        },
    },
];

interface DateRangeButtonsProps {
    onClick: (startDate: Date, endDate: Date, presetKey?: string) => void;
    startDate: Date;
    endDate: Date;
    activePresetKey?: string | null;
    presetDateButtonOptions:
        | { label: string; presetKey?: string; getDates(): { startDate: Date; endDate: Date } }[]
        | undefined;
}
export function DateRangeButtons({
    onClick,
    startDate,
    endDate,
    activePresetKey,
    presetDateButtonOptions = Buttons,
}: DateRangeButtonsProps) {
    const activeValue = useMemo(() => {
        if (activePresetKey != null) {
            const match = presetDateButtonOptions.find((preset) => preset.presetKey === activePresetKey);
            return match ? [match.label] : [];
        }
        const match = presetDateButtonOptions.find((preset) => {
            const { startDate: presetStart, endDate: presetEnd } = preset.getDates();
            return (
                Math.abs(startDate.getTime() - presetStart.getTime()) <= BUFFER_MS &&
                Math.abs(endDate.getTime() - presetEnd.getTime()) <= BUFFER_MS
            );
        });
        return match ? [match.label] : [];
    }, [startDate, endDate, activePresetKey, presetDateButtonOptions]);

    return (
        <ToggleGroup variant={"outline"} size={"lg"} value={activeValue}>
            {presetDateButtonOptions.map((dateRange, index) => (
                <Fragment key={dateRange.label}>
                    <ToggleGroupItem
                        value={dateRange.label}
                        aria-label={dateRange.label}
                        onClick={() => {
                            const dates = dateRange.getDates();
                            onClick(dates.startDate, dates.endDate, dateRange.presetKey);
                        }}
                    >
                        {dateRange.label}
                    </ToggleGroupItem>
                    {index < presetDateButtonOptions.length - 1 && <ToggleGroupSeparator />}
                </Fragment>
            ))}
        </ToggleGroup>
    );
}
